import * as choiceUtils from "./ChoiceUtils"

// Load the Base64 string for a file
export async function fileToBase64String(file: File) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
    });    
}

export function getFileExt(filename: string){
    const parts = filename.split(".");
    const ext  = parts[parts.length - 1].toLowerCase();
    return ext;
}

const fileTypes = [
    { ext: "jpg", isImage: true, mimeType: "image/jpeg" }, 
    { ext: "jpeg", isImage: true, mimeType: "image/jpeg" }, 
    { ext: "gif", isImage: true, mimeType: "image/gif"  }, 
    { ext: "png", isImage: true, mimeType: "image/png" }, 
    { ext: "bmp", isImage: true, mimeType: "image/bmp" }, 
    { ext: "tif", isImage: true, mimeType: "image/tif" }, 
//    { ext: "tiff", isImage: true, mimeType: "image/tif" },
    { ext: "pdf", isImage: true, mimeType: "application/pdf" },     // Not sure if we should treat PDF as image or not but has been working so far...
    { ext: "txt", isImage: false, mimeType: "text/plain" },
    { ext: "xml", isImage: false, mimeType: "text/xml" },
    { ext: "xml", isImage: false, mimeType: "application/xml" },
    { ext: "xls", isImage: false, mimeType: "application/vnd.ms-excel" },
    { ext: "xlsx", isImage: false, mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },    
    { ext: "doc", isImage: false, mimeType: "application/msword" },
    { ext: "docx", isImage: false, mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
];

// Get file info from a file name
export function fileTypeInfo(filename: string){
    const fileExt = getFileExt(filename);
    
    const fileType = fileTypes.find(fileType => fileType.ext == fileExt);
    return fileType;
}

// Get file extension from the content type
export function fileExtFromMimeType(contentType: string|undefined): string|undefined
{
    if (!contentType)
        return;
    return fileTypes.find(fileType => fileType.mimeType.toLowerCase() === contentType.toLowerCase())?.ext;
}

export function isImage(filename: string){

    const fileTypeInfoVal = fileTypeInfo(filename);
    if (!fileTypeInfoVal)
        return false;
    return fileTypeInfoVal.isImage;
}

export function mimeType(filename: string) {
    const fileTypeInfoVal = fileTypeInfo(filename);
    if (!fileTypeInfoVal)
        return;

    return fileTypeInfoVal.mimeType;
}

export function base64MimeType(filename: string){
    const mimeTypeVal = mimeType(filename);
    if (!mimeTypeVal)
        return;

    return `data:${mimeTypeVal};base64,`;
}

// Get the src property value for a base64 document (image/sound/etc...)
export function base64AndFileNameToUrl(base64String: string, filename: string){
    if (!base64String || 0 === base64String.length)
        return;

    // If we already have a Base64 data string, return it
    const dataPrefix = "data:";
    if (dataPrefix === base64String.substring(0, dataPrefix.length))
        return base64String;

    const mimeTypePrefix = base64MimeType(filename);
    if (!mimeTypePrefix)
        return;

    return `${mimeTypePrefix} ${base64String}`;
}

// Take the mime type and base64 string and convert to a valid URL
export function base64AndMimeTypeToUrl(base64String: string, mimeType: string)
{
    return "data:"+ mimeType + ";base64, " + base64String;
}
// Convert a  string to byte array
function stringToUint8Array(input: string): Uint8Array {
    const encoder = new TextEncoder();
    return encoder.encode(input);
}
// Save a base64 string as a file after showing a file picker
export async function saveBase64ToFileWithPicker(base64Data: string, suggestedFileName: string)
{
    const byteChars = window.atob(base64Data);
    const bytes = Uint8Array.from(byteChars, c => c.charCodeAt(0));

    await saveToFileWithPicker(bytes, suggestedFileName);
}

// Save a text file
export async function saveToTextFileWithPicker(data: string, suggestedName: string){
    const bData = stringToUint8Array(data);
    await saveToFileWithPicker(bData, suggestedName);
}
// Save a file
export async function saveToFileWithPicker(data: Uint8Array, suggestedName: string){
    const fileHandle = await (window as any).showSaveFilePicker({ suggestedName });
    const fileStream = await fileHandle.createWritable();

    // Write file
    await fileStream.write(data);
    await fileStream.close();
}

// Download a web based resource at the specified URL and use the specfied fileName,
// This version doesn't appear to be used, use downloadResourceAsync instead
export async function downloadResource(resourceUrl: string, saveToFileName: string)
{
    const a = document.createElement('a');
    const uniqueEleId = `Download_${choiceUtils.newGuid()}`;
    a.setAttribute('id', uniqueEleId);
    a.setAttribute('href', resourceUrl);
    a.setAttribute('download', saveToFileName);

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}
// Download a web based resource at the specified URL and use the specfied fileName
export async function downloadResourceAsync(url: string, fileName: string): Promise<void> {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch resource: ${response.statusText}`);
        }

        const blob = await response.blob();
        const link = document.createElement('a');
        const urlObject = URL.createObjectURL(blob);

        link.href = urlObject;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(urlObject);
    } catch (error) {
        console.error('Download failed:', error);
    }
}

// Add an extension (xxx or .xxx) to a filename while avoiding double "."
export function fileExtensionWithDot(ext?: string): string|undefined
{
    ext = ext?.trim();

    if (!ext)
        return;

    if (ext.startsWith("."))
        return ext;
    return "." + ext;
}

// Add an extension (xxx or .xxx) to a filename while avoiding double "."
export function combineFilenameAndExt(filename: string|undefined, ext: string|undefined): string|undefined
{
    if (!filename || !ext)
        return;

    if (ext.startsWith("."))
        return `${filename}${ext}`;
    return `${filename}.${ext}`;
}